import React from 'react';
import { formatCurrency, formatDate } from '@/utils/format';
import { getStatusConfig } from '@/utils/status';
import { Button, AuthButton, Badge } from '@/components/ui';
import type { ConstructionBoqVersion } from '../types/construction-boq.types';

interface BoqVersionDesktopTableProps {
  versions: ConstructionBoqVersion[];
  onViewDetail: (version: ConstructionBoqVersion) => void;
  onRestore: (version: ConstructionBoqVersion) => void;
}

export function BoqVersionDesktopTable({ versions, onViewDetail, onRestore }: BoqVersionDesktopTableProps) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
      <table className="w-full text-left border-collapse">
        <thead className="bg-slate-50 border-b border-slate-200 font-normal">
          <tr>
            <th className="px-6 py-4 text-[10px] font-normal text-slate-500 tracking-widest">Phiên bản</th>
            <th className="px-6 py-4 text-[10px] font-normal text-slate-500 tracking-widest">Tên / Ghi chú</th>
            <th className="px-6 py-4 text-[10px] font-normal text-slate-500 tracking-widest text-right">Giá trị</th>
            <th className="px-6 py-4 text-[10px] font-normal text-slate-500 tracking-widest text-center">Trạng thái</th>
            <th className="px-6 py-4 text-[10px] font-normal text-slate-500 tracking-widest">Ngày tạo</th>
            <th className="px-6 py-4 text-[10px] font-normal text-slate-500 tracking-widest text-right">Thao tác</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {versions.map((v) => {
            const status = getStatusConfig(v.status_code, {
              draft: { label: 'Bản nháp', variant: 'default', icon: 'ri-draft-line' },
              approved: { label: 'Đã duyệt', variant: 'success', icon: 'ri-check-line' },
              locked: { label: 'Đã khóa', variant: 'warning', icon: 'ri-lock-line' },
              archived: { label: 'Lưu trữ', variant: 'secondary', icon: 'ri-archive-line' }
            });

            return (
              <tr key={v.id} className="hover:bg-slate-50 transition-colors group">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-os-primary/10 flex items-center justify-center text-os-primary font-bold text-xs">
                      v{v.version_number}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm font-bold text-slate-900">{v.version_name}</div>
                  {v.description && <div className="text-xs text-slate-400 mt-0.5 line-clamp-1">{v.description}</div>}
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="text-sm font-bold text-slate-900">{formatCurrency(v.total_amount)}</div>
                </td>
                <td className="px-6 py-4 text-center">
                  <Badge variant={status.variant as any}>
                    {status.label}
                  </Badge>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-slate-600">{formatDate(v.created_at)}</div>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="sm" icon="ri-eye-line" onClick={() => onViewDetail(v)}>Xem</Button>
                    <AuthButton
                      module="boq"
                      action="update"
                      variant="outline"
                      size="sm"
                      icon="ri-history-line"
                      onClick={() => onRestore(v)}
                    >
                      Khôi phục
                    </AuthButton>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
